import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useStorefrontSizeAvailability - the BACKGROUND hint behind the product
 * page's "not available in this color" sizes.
 *
 *   GET /api/storefront/<slug>/products/<id>/size-availability
 *     -> { availability: { <color id>: { <size label>: null | n | 0 } } }
 *
 * `null` = plenty, an integer 1..24 = low stock (the count), `0` = not
 * available. Colors and sizes the server could not answer are absent.
 *
 * Contract (Jake, 2026-10-26, FAIL OPEN):
 *   - `fetchAvailability` never rejects and never throws into the page. Any
 *     failure, abort or malformed body leaves `availability` as `{}`, which
 *     the page reads as "nothing is disabled" -- exactly today's behavior.
 *   - A response that is no longer the latest request is ignored (the
 *     shopper navigated to another product), and `availability` is cleared
 *     the moment a new fetch starts so one product's map never bleeds onto
 *     another.
 *   - It is a UI hint only. Checkout does not consult it.
 *
 * The map covers every color of the product, so a color change needs no
 * new request; the page just reads the selected color's entry.
 */
export function useStorefrontSizeAvailability() {
    const availability = ref({})

    let generation = 0
    let currentAbort = null

    const fetchAvailability = async (slug, id) => {
        if (currentAbort) {
            currentAbort.abort()
            currentAbort = null
        }
        const mine = ++generation
        availability.value = {}

        const missing = (v) => v === undefined || v === null || v === ''
        if (missing(slug) || missing(id)) return

        const controller =
            typeof AbortController !== 'undefined' ? new AbortController() : null
        currentAbort = controller

        try {
            const response = await axiosInstance.get(
                `/api/storefront/${encodeURIComponent(slug)}/products/${encodeURIComponent(id)}/size-availability`,
                controller ? { signal: controller.signal } : undefined
            )
            if (mine !== generation) return
            availability.value = sanitize(response?.data?.availability)
        } catch (_err) {
            // Fail open: no hint is the same as today's page.
            if (mine === generation) availability.value = {}
        } finally {
            if (mine === generation) currentAbort = null
        }
    }

    return { availability, fetchAvailability }
}

/**
 * Keep only `{color: {size: null | integer 0..}}`. The page trusts this
 * shape, so anything else is dropped rather than interpreted.
 */
function sanitize(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
    const out = {}
    for (const [color, sizes] of Object.entries(raw)) {
        if (!sizes || typeof sizes !== 'object' || Array.isArray(sizes)) continue
        const levels = {}
        for (const [size, level] of Object.entries(sizes)) {
            if (level === null || (Number.isInteger(level) && level >= 0)) {
                levels[size] = level
            }
        }
        if (Object.keys(levels).length > 0) out[color] = levels
    }
    return out
}
